import type { WorkerRow } from '@facture/contracts'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { HardHatIcon, PlusIcon, UsersIcon, WalletIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { AnimatedNumber, Stagger, StaggerItem } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import { ClearFilters, FilterBar, FilterSearch, FilterSelect, FilterTabs } from '@/components/ui/filter-bar'
import { Avatar } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { positionsQuery, workersQuery } from '@/features/payroll/api'
import { fmtCol, PayrollDisclaimer } from '@/features/payroll/payroll-ui'
import { WorkerSheet } from '@/features/payroll/worker-sheet'
import { formatDate, formatIdentification } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  position: z.string().optional(),
  balance: z.enum(['with']).optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/projects/workers/')({
  staticData: { crumb: 'Trabajadores' },
  validateSearch: searchSchema,
  component: WorkersPage,
})

const col = columnHelper<WorkerRow>()

function WorkersPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCan('payroll:write')
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [creating, setCreating] = useState(false)
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const status = search.status ?? 'active'
  const list = useQuery(
    workersQuery({
      search: search.q,
      status,
      positionId: search.position,
      withBalance: search.balance === 'with' ? true : undefined,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const positions = useQuery(positionsQuery)
  const kpis = list.data?.kpis

  const columns = useMemo(
    () => [
      col.accessor('name', {
        header: 'Trabajador',
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={row.original.name} className="hidden size-9 sm:flex" />
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.name}</div>
              <div className="truncate font-mono text-muted-foreground text-xs">
                {row.original.identification
                  ? formatIdentification(row.original.identificationType, row.original.identification)
                  : 'Sin identificación'}
              </div>
            </div>
          </div>
        ),
      }),
      col.accessor('defaultPositionName', {
        header: 'Puesto habitual',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) =>
          getValue() ? (
            <span className="text-sm">{getValue()}</span>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          ),
      }),
      col.accessor('activeProjects', {
        header: 'Proyectos',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ getValue }) =>
          getValue() ? (
            <Badge variant="warning">
              <HardHatIcon /> {getValue()}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs">Sin asignar</span>
          ),
      }),
      col.accessor('lastEntryDate', {
        header: 'Último registro',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <span className="text-muted-foreground tabular">
            {getValue() ? formatDate(getValue(), 'd MMM yy') : '—'}
          </span>
        ),
      }),
      col.accessor('balance', {
        header: 'Saldo por pagar',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            {!row.original.isActive && <Badge variant="neutral">Inactivo</Badge>}
            <span
              className={cn(
                'font-medium',
                row.original.balance > 0 ? 'text-foreground' : 'text-muted-foreground',
                row.original.balance < 0 && 'text-destructive',
              )}
            >
              {fmtCol(row.original.balance)}
            </span>
          </div>
        ),
      }),
    ],
    [],
  )

  const filtered = !!(
    search.q ||
    search.position ||
    search.balance ||
    (search.status && search.status !== 'active')
  )

  return (
    <Page>
      <PageHeader
        title="Trabajadores"
        description="El personal de las obras: sus datos, en qué proyectos está y cuánto se le debe."
        actions={
          canWrite && (
            <Button onClick={() => setCreating(true)}>
              <PlusIcon /> Nuevo trabajador
            </Button>
          )
        }
      />
      <Stagger className="mb-4 grid gap-4 sm:grid-cols-3">
        <Kpi label="Activos" icon={UsersIcon} value={kpis?.active} count />
        <Kpi label="Con saldo por pagar" icon={HardHatIcon} value={kpis?.withBalance} count />
        <Kpi label="Saldo total por pagar" icon={WalletIcon} value={kpis?.balance} accent />
      </Stagger>
      <PayrollDisclaimer className="mb-4" />
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(w) => w.id}
        onRowClick={(w) => navigate({ to: '/projects/workers/$id', params: { id: w.id } })}
        isRowMuted={(w) => !w.isActive}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Estado"
              value={status}
              onValueChange={(v) => setSearch({ status: v === 'active' ? undefined : v, page: undefined })}
              options={[
                { value: 'active', label: 'Activos' },
                { value: 'inactive', label: 'Inactivos' },
                { value: 'all', label: 'Todos' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por nombre o cédula…" />
            <FilterSelect
              label="Puesto"
              width="lg"
              value={search.position ?? 'all'}
              onValueChange={(v) => setSearch({ position: v === 'all' ? undefined : v, page: undefined })}
              options={[
                { value: 'all', label: 'Todos los puestos' },
                ...(positions.data ?? []).map((p) => ({ value: p.id, label: p.name })),
              ]}
            />
            <FilterSelect
              label="Saldo"
              width="lg"
              value={search.balance ?? 'all'}
              onValueChange={(v) =>
                setSearch({ balance: v === 'with' ? 'with' : undefined, page: undefined })
              }
              options={[
                { value: 'all', label: 'Con o sin saldo' },
                { value: 'with', label: 'Con saldo por pagar' },
              ]}
            />
            <ClearFilters
              active={filtered}
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  status: undefined,
                  position: undefined,
                  balance: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={UsersIcon}
            title={filtered ? 'Sin trabajadores con esos filtros' : 'Todavía no hay trabajadores'}
            description={
              filtered
                ? 'Probá con otro estado o limpiá la búsqueda.'
                : 'Registrá al personal y asignalo a los proyectos para llevar sus horas y su saldo.'
            }
            action={
              canWrite &&
              !filtered && (
                <Button onClick={() => setCreating(true)}>
                  <PlusIcon /> Nuevo trabajador
                </Button>
              )
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <WorkerSheet
        open={creating}
        onOpenChange={setCreating}
        onSaved={(w) => navigate({ to: '/projects/workers/$id', params: { id: w.id } })}
      />
    </Page>
  )
}

function Kpi({
  label,
  icon: Icon,
  value,
  count,
  accent,
}: {
  label: string
  icon: typeof UsersIcon
  value?: number
  count?: boolean
  accent?: boolean
}) {
  return (
    <StaggerItem>
      <Card className="relative h-full overflow-hidden p-5">
        {accent && (
          <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-warning/20 blur-3xl" />
        )}
        <div className="relative">
          <div className="flex items-center justify-between text-muted-foreground text-sm">
            {label}
            <span
              className={cn(
                'flex size-8 items-center justify-center rounded-lg',
                accent ? 'bg-warning-soft text-warning-soft-foreground' : 'bg-muted',
              )}
            >
              <Icon className="size-4" />
            </span>
          </div>
          {value === undefined ? (
            <Skeleton className="mt-3 h-8 w-32" />
          ) : (
            <div className="mt-2 truncate font-semibold text-[26px] tracking-tight tabular">
              <AnimatedNumber value={value} format={count ? (n) => String(Math.round(n)) : fmtCol} />
            </div>
          )}
        </div>
      </Card>
    </StaggerItem>
  )
}
